import React, { useState, useCallback, useRef } from "react";

import axios from "axios";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5001/api";


export default function WhatYouCanDo() {
  const [zipCode, setZipCode] = useState("");
  const [representatives, setRepresentatives] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [locationLoading, setLocationLoading] = useState(false);
  const [selectedRep, setSelectedRep] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [messageBody, setMessageBody] = useState("");
  const [messageSubject, setMessageSubject] = useState("");
  const resultsRef = useRef(null);

  // Fetch representatives by ZIP code
  const handleFindRep = useCallback(async (zip) => {
    const targetZip = zip || zipCode;
    if (!/^\d{5}$/.test(targetZip)) {
      setError("Please enter a valid 5-digit US ZIP code.");
      return;
    }
    setLoading(true);
    setError("");
    setRepresentatives(null);
    setSelectedRep(null);

    try {
      const response = await axios.get(`${API_BASE}/representatives/zip/${targetZip}`);
      if (response.data.success) {
        setRepresentatives(response.data);
        // Auto-scroll to results
        setTimeout(() => {
          resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 300);
      }
    } catch (err) {
      const msg = err.response?.data?.error || "Failed to find representatives. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [zipCode]);

  // Use geolocation
  const handleUseLocation = useCallback(async () => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by your browser.");
      return;
    }

    setLocationLoading(true);
    setError("");
    setRepresentatives(null);
    setSelectedRep(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const response = await axios.post(`${API_BASE}/representatives/geolocation`, {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
          if (response.data.success) {
            setRepresentatives(response.data);
            setZipCode(response.data.zip || "");
          }
        } catch (err) {
          const msg = err.response?.data?.error || "Failed to determine location. Please enter ZIP manually.";
          setError(msg);
        } finally {
          setLocationLoading(false);
        }
      },
      (geoError) => {
        setLocationLoading(false);
        if (geoError.code === 1) setError("Location access denied. Please enter your ZIP code manually.");
        else if (geoError.code === 2) setError("Location unavailable. Please enter your ZIP code manually.");
        else setError("Location request timed out. Please enter your ZIP code manually.");
      },
      { timeout: 10000, enableHighAccuracy: false }
    );
  }, []);



  // Open contact modal for a representative
  const handleContactRep = useCallback(async (rep) => {
    setSelectedRep(rep);
    setCopied(false);

    // Generate template from backend
    try {
      const response = await axios.post(`${API_BASE}/email/generate-template`, {
        representativeName: rep.name,
        district: rep.district,
        stateName: rep.stateName,
        senderName: "",
        repType: rep.type,
      });
      if (response.data.success) {
        setMessageSubject(response.data.subject);
        setMessageBody(response.data.body);
      }
    } catch {
      // Fallback template
      const lastName = rep.name.split(" ").pop();
      const title = rep.type === "Senator" ? "Senator" : "Representative";
      setMessageSubject("Urgent: Address the Prescription Drug Price Crisis");
      setMessageBody(`Dear ${title} ${lastName},

I am writing as your constituent to express my deep concern about the prescription drug pricing crisis affecting Americans.

Americans pay 256% more for prescription drugs compared to other countries. 1 in 4 Americans cannot afford their prescribed medications.

I urge you to take immediate action to address these skyrocketing costs.

Sincerely,
[Your Name]`);
    }

    setIsModalOpen(true);
  }, []);

  // Copy message to clipboard
  const handleCopyMessage = useCallback(async () => {
    try {
      const fullMessage = `Subject: ${messageSubject}\n\n${messageBody}`;
      await navigator.clipboard.writeText(fullMessage);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = `Subject: ${messageSubject}\n\n${messageBody}`;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  }, [messageSubject, messageBody]);

  const allReps = representatives
    ? [...(representatives.senators || []), ...(representatives.representatives || [])]
    : [];

  return (
    <>
      {/* Find Your Representative Section */}
      <div style={{ background: "white", padding: "60px 20px" }}>
        <div style={{ maxWidth: "940px", margin: "0 auto", background: "#ffffff", borderRadius: "16px", padding: "40px", boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)", border: "1px solid #f3f4f6" }}>
          <h2 style={{ fontSize: "1.75rem", fontWeight: "700", textAlign: "center", marginBottom: "10px", color: "#111827" }}>
            Find Your Representative
          </h2>
          <p style={{ textAlign: "center", color: "#6b7280", marginBottom: "30px", fontSize: "0.95rem" }}>
            Enter your 5-digit ZIP code or use your location to find your U.S. Congress members
          </p>

          {/* Search Controls */}
          <div style={{ display: "flex", gap: "12px", maxWidth: "600px", margin: "0 auto 20px", flexWrap: "wrap", justifyContent: "center" }}>
            <div style={{ display: "flex", flex: "1", minWidth: "280px", border: "1px solid #e5e7eb", borderRadius: "8px", overflow: "hidden" }}>
              <input
                type="text"
                placeholder="Enter ZIP code (e.g. 10001)"
                value={zipCode}
                onChange={(e) => { setZipCode(e.target.value.replace(/\D/g, "").slice(0, 5)); setError(""); }}
                onKeyDown={(e) => e.key === "Enter" && handleFindRep()}
                style={{ flex: "1", padding: "12px 20px", fontSize: "1rem", border: "none", outline: "none" }}
              />
              <button
                onClick={() => handleFindRep()}
                disabled={loading}
                style={{ padding: "12px 25px", background: loading ? "#94a3b8" : "#43C5B9", color: "#ffffff", fontSize: "1rem", fontWeight: "600", border: "none", cursor: loading ? "default" : "pointer", whiteSpace: "nowrap" }}
              >
                {loading ? "Searching..." : "Find Rep"}
              </button>
            </div>
            <button
              onClick={handleUseLocation}
              disabled={locationLoading}
              style={{
                padding: "12px 20px", background: locationLoading ? "#94a3b8" : "#1e40af", color: "#fff",
                fontSize: "0.95rem", fontWeight: "600", border: "none", borderRadius: "8px",
                cursor: locationLoading ? "default" : "pointer", display: "flex", alignItems: "center", gap: "8px",
                whiteSpace: "nowrap", transition: "background 0.2s ease"
              }}
            >
              📍 {locationLoading ? "Detecting..." : "Use My Location"}
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px", padding: "12px 16px", marginBottom: "20px", maxWidth: "600px", margin: "0 auto 20px", display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ color: "#dc2626", fontSize: "14px" }}>⚠️ {error}</span>
            </div>
          )}



          {/* Loading Indicator */}
          {loading && (
            <div style={{ textAlign: "center", padding: "30px" }}>
              <div style={{ display: "inline-block", width: "40px", height: "40px", border: "4px solid #e5e7eb", borderTopColor: "#43C5B9", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
              <p style={{ marginTop: "12px", color: "#6b7280" }}>Finding your representatives...</p>
            </div>
          )}

          {/* Results */}
          {representatives && !loading && (
            <div ref={resultsRef}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "24px", padding: "16px 20px", background: "#ecfdf5", borderRadius: "10px", border: "1px solid #a7f3d0" }}>
                <span style={{ fontSize: "24px" }}>🏛️</span>
                <div>
                  <h3 style={{ fontSize: "1.2rem", fontWeight: "700", color: "#065f46", margin: 0 }}>
                    {representatives.stateName}
                  </h3>
                  <p style={{ fontSize: "0.9rem", color: "#047857", margin: 0 }}>
                    {representatives.senators?.length || 0} Senators · {representatives.representatives?.length || 0} House Representatives
                    {representatives.zip && ` · ZIP ${representatives.zip}`}
                  </p>
                </div>
              </div>

              {/* Senators Section */}
              {representatives.senators?.length > 0 && (
                <>
                  <h3 style={{ fontSize: "1.1rem", fontWeight: "700", color: "#111827", marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ background: "#dbeafe", color: "#1e40af", padding: "2px 10px", borderRadius: "20px", fontSize: "0.8rem" }}>Senate</span>
                    U.S. Senators
                  </h3>
                  <CardSlider items={representatives.senators} onSendEmail={handleContactRep} />
                </>
              )}

              {/* House Representatives Section */}
              {representatives.representatives?.length > 0 && (
                <>
                  <h3 style={{ fontSize: "1.1rem", fontWeight: "700", color: "#111827", marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ background: "#fce7f3", color: "#be185d", padding: "2px 10px", borderRadius: "20px", fontSize: "0.8rem" }}>House</span>
                    House Representatives ({representatives.representatives.length})
                  </h3>
                  <CardSlider items={representatives.representatives} onSendEmail={handleContactRep} />
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <div style={{ position: "relative" }}>
        {/* What You Can Do Section */}
        <section style={{ padding: "60px 20px", background: "#fff" }}>
          <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
            <h2 style={{ fontSize: "1.75rem", fontWeight: "800", color: "#111827", marginBottom: "30px", textAlign: "left" }}>
              4 Steps for Change
            </h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "20px 40px" }}>
              {[
                { icon: "1", bg: "#e0f8f6", color: "#43C5B9", title: "Find Your Representatives", desc: "Enter your ZIP code to identify your elected officials." },
                { icon: "3", bg: "#d1fae5", color: "#059669", title: "Copy a Pre-Written Message", desc: "Use our ready-made template highlighting real drug price disparities." },
                { icon: "2", bg: "#ede9fe", color: "#7c3aed", title: "Select Who to Contact", desc: "Choose the representative you’d like to send a message to.." },
                { icon: "4", bg: "#ffedd5", color: "#ea580c", title: "Send Your Message", desc: "Paste the message into your representative’s contact form and submit." },
              ].map((item, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                  <span style={{ fontSize: "20px", background: item.bg, color: item.color, borderRadius: "8px", padding: "8px", display: "flex", alignItems: "center", justifyContent: "center", minWidth: "36px", height: "36px" }}>
                    {item.icon}
                  </span>
                  <div>
                    <h3 style={{ fontSize: "1rem", fontWeight: "700", color: "#111827", margin: "0 0 4px 0" }}>{item.title}</h3>
                    <p style={{ fontSize: "0.9rem", color: "#4b5563", margin: 0 }}>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>



        {/* Contact Modal */}
        {isModalOpen && selectedRep && (
          <div
            onClick={(e) => { if (e.target === e.currentTarget) setIsModalOpen(false); }}
            style={{
              position: "fixed", inset: 0,
              background: "rgba(8, 15, 28, 0.72)",
              display: "flex", alignItems: "center", justifyContent: "center",
              zIndex: 1000, padding: "16px", boxSizing: "border-box",
              animation: "overlayIn 0.18s ease-out",
            }}
          >
            <div className="msg-modal" style={{
              display: "flex", width: "100%", maxWidth: "780px",
              maxHeight: "90vh", borderRadius: "4px",
              overflow: "hidden",
              boxShadow: "0 24px 80px rgba(0,0,0,0.4)",
              animation: "modalIn 0.22s ease-out",
            }}>
              {/* ── LEFT PANEL ── */}
              <div style={{
                width: "240px", flexShrink: 0,
                background: "#0e1f38",
                display: "flex", flexDirection: "column",
                padding: "32px 24px",
                boxSizing: "border-box",
              }} className="msg-modal-left">
                {/* Teal rule */}
                <div style={{ width: "28px", height: "3px", background: "#43C5B9", marginBottom: "24px" }} />

                <p style={{ fontSize: "0.65rem", fontWeight: "700", color: "#43C5B9", textTransform: "uppercase", letterSpacing: "0.14em", margin: "0 0 12px" }}>
                  Recipient
                </p>
                <p style={{ fontSize: "1rem", fontWeight: "700", color: "#fff", margin: "0 0 4px", lineHeight: 1.3 }}>
                  {selectedRep.name}
                </p>
                <p style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.45)", margin: "0 0 6px", lineHeight: 1.5 }}>
                  {selectedRep.type}{selectedRep.district ? `, District ${selectedRep.district}` : ""}
                </p>
                <p style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.35)", margin: "0 0 32px" }}>
                  {selectedRep.stateName} · {selectedRep.party}
                </p>

                {/* Divider */}
                <div style={{ height: "1px", background: "rgba(255,255,255,0.08)", marginBottom: "28px" }} />

                <p style={{ fontSize: "0.65rem", fontWeight: "700", color: "#43C5B9", textTransform: "uppercase", letterSpacing: "0.14em", margin: "0 0 16px" }}>
                  Steps
                </p>
                {[
                  "Personalize the message",
                  "Copy to clipboard",
                  "Open their contact form",
                  "Paste and submit",
                ].map((step, i) => (
                  <div key={i} style={{ display: "flex", gap: "10px", alignItems: "flex-start", marginBottom: "14px" }}>
                    <span style={{
                      fontSize: "0.65rem", fontWeight: "800", color: "#0e1f38",
                      background: "#43C5B9", borderRadius: "2px",
                      minWidth: "18px", height: "18px",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0, marginTop: "1px",
                    }}>{i + 1}</span>
                    <span style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.5)", lineHeight: 1.5 }}>{step}</span>
                  </div>
                ))}

                {/* Phone link if available */}
                {selectedRep.phone && selectedRep.phone !== "N/A" && (
                  <>
                    <div style={{ height: "1px", background: "rgba(255,255,255,0.08)", margin: "16px 0 20px" }} />
                    <p style={{ fontSize: "0.65rem", fontWeight: "700", color: "#43C5B9", textTransform: "uppercase", letterSpacing: "0.14em", margin: "0 0 8px" }}>
                      Direct Phone
                    </p>
                    <p style={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.55)", margin: 0 }}>
                      {selectedRep.phone}
                    </p>
                  </>
                )}
              </div>

              {/* ── RIGHT PANEL ── */}
              <div style={{
                flex: 1, background: "#ffffff",
                display: "flex", flexDirection: "column",
                overflow: "hidden",
              }}>
                {/* Right header bar */}
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "20px 28px", borderBottom: "1px solid #f0f0f0",
                  flexShrink: 0,
                }}>
                  <span style={{ fontSize: "0.78rem", fontWeight: "600", color: "#9ca3af", letterSpacing: "0.04em" }}>
                    Draft your message
                  </span>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="modal-close-btn"
                    style={{
                      background: "none", border: "none", padding: "4px 8px",
                      fontSize: "1.1rem", cursor: "pointer", color: "#c4c9d1",
                      lineHeight: 1, display: "flex", alignItems: "center",
                      transition: "color 0.15s",
                    }}
                  >✕</button>
                </div>

                {/* Scrollable form body */}
                <div style={{ flex: 1, overflowY: "auto", padding: "24px 28px 28px" }}>
                  {/* Subject */}
                  <div style={{ marginBottom: "18px" }}>
                    <label style={{
                      display: "block", fontSize: "0.7rem", fontWeight: "700",
                      color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "7px",
                    }}>Subject</label>
                    <input
                      type="text"
                      value={messageSubject}
                      onChange={(e) => setMessageSubject(e.target.value)}
                      className="msg-input"
                      style={{
                        width: "100%", padding: "10px 14px", boxSizing: "border-box",
                        border: "1px solid #e4e7eb", borderRadius: "3px",
                        fontSize: "0.9rem", color: "#111827", outline: "none",
                        fontFamily: "inherit", background: "#fafafa",
                        transition: "border-color 0.15s, background 0.15s",
                      }}
                      onFocus={(e) => { e.target.style.borderColor = "#43C5B9"; e.target.style.background = "#fff"; }}
                      onBlur={(e) => { e.target.style.borderColor = "#e4e7eb"; e.target.style.background = "#fafafa"; }}
                    />
                  </div>

                  {/* Body */}
                  <div style={{ marginBottom: "22px" }}>
                    <label style={{
                      display: "block", fontSize: "0.7rem", fontWeight: "700",
                      color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: "7px",
                    }}>Message</label>
                    <textarea
                      value={messageBody}
                      onChange={(e) => setMessageBody(e.target.value)}
                      className="msg-input"
                      style={{
                        width: "100%", height: "220px", padding: "12px 14px", boxSizing: "border-box",
                        border: "1px solid #e4e7eb", borderRadius: "3px",
                        fontSize: "0.86rem", color: "#1f2937", lineHeight: "1.75",
                        resize: "vertical", outline: "none",
                        fontFamily: "inherit", background: "#fafafa",
                        transition: "border-color 0.15s, background 0.15s",
                      }}
                      onFocus={(e) => { e.target.style.borderColor = "#43C5B9"; e.target.style.background = "#fff"; }}
                      onBlur={(e) => { e.target.style.borderColor = "#e4e7eb"; e.target.style.background = "#fafafa"; }}
                    />
                  </div>

                  {/* Action row */}
                  <div style={{ display: "flex", gap: "10px" }}>
                    <button
                      onClick={handleCopyMessage}
                      className="btn-primary"
                      style={{
                        flex: 1, padding: "12px 20px",
                        background: copied ? "#1a9e5c" : "#0e1f38",
                        color: "#fff", border: "none", borderRadius: "3px",
                        fontSize: "0.86rem", fontWeight: "600", cursor: "pointer",
                        letterSpacing: "0.02em", transition: "background 0.2s",
                      }}
                    >
                      {copied ? "Copied to Clipboard" : "Copy Message"}
                    </button>
                    <button
                      onClick={() => {
                        const url = selectedRep?.contactForm || selectedRep?.website || "";
                        if (url) window.open(url, "_blank");
                      }}
                      className="btn-secondary"
                      style={{
                        flex: 1, padding: "12px 20px",
                        background: "#fff", color: "#0e1f38",
                        border: "1.5px solid #0e1f38", borderRadius: "3px",
                        fontSize: "0.86rem", fontWeight: "600", cursor: "pointer",
                        letterSpacing: "0.02em", transition: "background 0.15s, color 0.15s",
                      }}
                    >
                      Open Contact Form
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <style>{`
          @keyframes overlayIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes modalIn {
            from { opacity: 0; transform: translateY(20px) scale(0.98); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          .modal-close-btn:hover { color: #374151 !important; }
          .btn-primary:hover { background: #1a3a5c !important; }
          .btn-secondary:hover { background: #0e1f38 !important; color: #fff !important; }
          @media (max-width: 600px) {
            .msg-modal { flex-direction: column !important; max-height: 95vh !important; }
            .msg-modal-left { width: 100% !important; padding: 20px !important; flex-direction: row !important; flex-wrap: wrap; gap: 12px; }
          }
          @media (max-width: 768px) {
            div[style*="grid-template-columns: repeat(2, 1fr)"] {
              grid-template-columns: 1fr !important;
            }
          }
          @media (max-width: 480px) {
            div[style*="grid-template-columns: 1fr 1fr"] {
              grid-template-columns: 1fr !important;
            }
          }
        `}</style>
      </div>
    </>
  );
}

/* Card Slider Component */
function CardSlider({ items, onSendEmail }) {
  const scrollRef = useRef(null);

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    const cardWidth = 400;
    scrollRef.current.scrollBy({ left: direction === "left" ? -cardWidth : cardWidth, behavior: "smooth" });
  };

  const showArrows = items.length > 2;

  return (
    <div style={{ position: "relative", marginBottom: "30px" }}>
      {/* Left Arrow */}
      {showArrows && (
        <button
          onClick={() => scroll("left")}
          style={{
            position: "absolute", left: "-18px", top: "50%", transform: "translateY(-50%)", zIndex: 2,
            width: "36px", height: "36px", borderRadius: "50%", border: "1px solid #e5e7eb",
            background: "white", boxShadow: "0 2px 8px rgba(0,0,0,0.12)", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", color: "#374151"
          }}
        >
          ‹
        </button>
      )}

      {/* Scrollable Track */}
      <div
        ref={scrollRef}
        className="slider-track"
        style={{
          display: "flex", gap: "16px", overflowX: "auto", scrollSnapType: "x mandatory",
          scrollBehavior: "smooth", padding: "4px 0"
        }}
      >
        {items.map((rep) => (
          <div key={rep.id} style={{ flex: "0 0 auto", width: "380px", scrollSnapAlign: "start" }}>
            <RepCard rep={rep} onSendEmail={onSendEmail} />
          </div>
        ))}
      </div>

      {/* Right Arrow */}
      {showArrows && (
        <button
          onClick={() => scroll("right")}
          style={{
            position: "absolute", right: "-18px", top: "50%", transform: "translateY(-50%)", zIndex: 2,
            width: "36px", height: "36px", borderRadius: "50%", border: "1px solid #e5e7eb",
            background: "white", boxShadow: "0 2px 8px rgba(0,0,0,0.12)", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", color: "#374151"
          }}
        >
          ›
        </button>
      )}

      {/* Dot indicators */}
      {showArrows && (
        <div style={{ display: "flex", justifyContent: "center", gap: "6px", marginTop: "14px" }}>
          {items.map((_, i) => (
            <div key={i} style={{ width: "8px", height: "8px", borderRadius: "50%", background: i === 0 ? "#43C5B9" : "#d1d5db", transition: "background 0.3s" }} />
          ))}
        </div>
      )}

      {/* Hide scrollbar CSS */}
      <style>{`
        .slider-track::-webkit-scrollbar { display: none; }
        .slider-track { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}

/* Representative Card Component */
function RepCard({ rep, onSendEmail }) {
  const [imgError, setImgError] = useState(false);
  const partyColor = rep.party === "Democrat" ? "#2563eb" : rep.party === "Republican" ? "#dc2626" : "#6b7280";
  const partyBg = rep.party === "Democrat" ? "#dbeafe" : rep.party === "Republican" ? "#fee2e2" : "#f3f4f6";

  return (
    <div style={{
      background: "#ffffff", borderRadius: "12px", padding: "20px", border: "1px solid #e5e7eb",
      display: "flex", gap: "16px", alignItems: "flex-start",
      transition: "box-shadow 0.2s ease, transform 0.2s ease", cursor: "default",
      boxShadow: "0 1px 3px rgba(0,0,0,0.06)", height: "100%", boxSizing: "border-box"
    }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 15px rgba(0,0,0,0.1)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.06)"; e.currentTarget.style.transform = "translateY(0)"; }}
    >
      {/* Photo */}
      <div style={{ width: "64px", height: "64px", borderRadius: "50%", overflow: "hidden", flexShrink: 0, background: "#e5e7eb", border: "2px solid #f3f4f6" }}>
        {!imgError ? (
          <img
            src={rep.photoUrl}
            alt={rep.name}
            onError={() => setImgError(true)}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: "#d1d5db", color: "#6b7280", fontSize: "24px" }}>
            👤
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px", flexWrap: "wrap" }}>
          <h4 style={{ fontSize: "1rem", fontWeight: "700", color: "#111827", margin: 0 }}>{rep.name}</h4>
          <span style={{ fontSize: "0.7rem", fontWeight: "700", color: partyColor, background: partyBg, padding: "2px 8px", borderRadius: "12px" }}>
            {rep.party === "Democrat" ? "D" : rep.party === "Republican" ? "R" : rep.party?.[0] || "I"}
          </span>
        </div>
        <p style={{ fontSize: "0.85rem", color: "#43C5B9", fontWeight: "600", margin: "0 0 4px" }}>
          {rep.type}{rep.district ? ` · District ${rep.district}` : ""} · {rep.stateName}
        </p>
        <p style={{ fontSize: "0.8rem", color: "#6b7280", margin: "0 0 10px" }}>
          {rep.phone !== "N/A" && `📞 ${rep.phone}`}
          {rep.phone !== "N/A" && rep.website && " · "}
          {rep.website && <a href={rep.website} target="_blank" rel="noopener noreferrer" style={{ color: "#2563eb", textDecoration: "none" }}>🌐 Website</a>}
        </p>
        <button
          onClick={() => onSendEmail(rep)}
          style={{
            padding: "8px 16px", background: "#ef4444", color: "#fff", fontSize: "0.85rem",
            fontWeight: "700", border: "none", borderRadius: "6px", cursor: "pointer",
            transition: "background 0.2s ease", display: "flex", alignItems: "center", gap: "6px"
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = "#dc2626"}
          onMouseLeave={(e) => e.currentTarget.style.background = "#ef4444"}
        >
          ✉️ Send Message
        </button>
      </div>
    </div>
  );
}